import React from 'react';

import {
  Box,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { FormikProvider } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import canAccessProcessingActivitiesAdmin from 'config/permissions/canAccessProcessingActivitiesAdmin';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import ProcessingActivityCategoryField from './_components/ProcessingActivityCategoryField';
import ProcessingActivityPublishDialog from './_components/ProcessingActivityPublishDialog';
import ProcessingActivityPurposesSection from './_components/ProcessingActivityPurposesSection';
import { PROCESSING_ROLE_OPTIONS } from './processingActivityEditor.constants';
import useStyles from './ProcessingActivityEditorPage.styles';
import useProcessingActivityEditor from './useProcessingActivityEditor';

const ProcessingActivityEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const editMatch = useRouteMatch<{ id: string }>(PATHS.PROCESSING_ACTIVITIES_EDIT);
  const processingActivityId = editMatch ? Number(editMatch.params.id) : undefined;
  const isCreateMode = !editMatch;

  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canManageProcessingActivities = canAccessProcessingActivitiesAdmin(userData);

  const {
    basePublishedVersionNumber,
    canDiscardDraft,
    canPublish,
    discardDraft,
    draftQuery,
    formik,
    handleNavigation,
    handlePublishClick,
    isSaving,
    NavigationPrompt,
    publishDisabledTooltip,
    publishDialogOpen,
    publishDraft,
    publishValidationErrors,
    ropaCategoriesByType,
    saveDraft,
    setPublishDialogOpen,
  } = useProcessingActivityEditor({
    isCreateMode,
    organizationId,
    processingActivityId,
  });

  const isLoading = !isCreateMode && draftQuery.isLoading;
  const isForbidden = !canManageProcessingActivities;

  const pageTitle = isCreateMode
    ? t(processing_activities_messages.editor.create_title)
    : t(processing_activities_messages.editor.edit_title);

  const pageSubtitle = isCreateMode
    ? t(processing_activities_messages.editor.create_subtitle)
    : t(processing_activities_messages.editor.edit_subtitle);

  if (isForbidden) {
    return (
      <Container>
        <Box className={styles.forbiddenState}>
          <Typography gutterBottom variant='h6'>
            {t(processing_activities_messages.forbidden_title)}
          </Typography>
          <Typography color='textSecondary' variant='body2'>
            {t(processing_activities_messages.forbidden_body)}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Loader inner />
      </Container>
    );
  }

  if (!isCreateMode && draftQuery.isError) {
    return (
      <Container>
        <Box className={styles.errorBanner}>
          <Typography color='error' gutterBottom variant='body2'>
            {t(processing_activities_messages.editor.errors.load_failed)}
          </Typography>
          <TextButton onClick={() => draftQuery.refetch()}>{t(general_messages.retry)}</TextButton>
        </Box>
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <NavigationPrompt />
        <ManagePageHeader backButton noBorder onBack={handleNavigation} subtitle={pageSubtitle}>
          {pageTitle}
        </ManagePageHeader>

        {!isCreateMode && (
          <div className={styles.statusRow}>
            <span className={styles.draftBadge}>{t(processing_activities_messages.draft_badge)}</span>
            {basePublishedVersionNumber != null && (
              <span className={styles.publishedVersion}>
                {t(processing_activities_messages.editor.published_version, { number: basePublishedVersionNumber })}
              </span>
            )}
          </div>
        )}

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(processing_activities_messages.editor.page_help)}
        </Typography>

        <FormikProvider value={formik}>
          <form onSubmit={event => event.preventDefault()}>
            <div className={styles.formCard}>
              <div className={styles.formHeader}>
                <div>
                  <Typography variant='h6'>{formik.values.name || t(processing_activities_messages.editor.new_activity_title)}</Typography>
                  <Typography className={styles.formSubtitle} variant='body2'>
                    {t(processing_activities_messages.editor.form_subtitle)}
                  </Typography>
                </div>
              </div>

              <section className={styles.section}>
                <Typography className={styles.sectionTitle} variant='subtitle1'>
                  {t(processing_activities_messages.editor.sections.core_information)}
                </Typography>
                <div className={styles.twoColumnGrid}>
                  <TextField
                    className={styles.fieldSpacing}
                    error={Boolean(publishValidationErrors.name)}
                    fullWidth
                    helperText={publishValidationErrors.name}
                    label={t(processing_activities_messages.editor.fields.name)}
                    name='name'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    variant='outlined'
                  />
                  <FormControl
                    className={styles.fieldSpacing}
                    error={Boolean(publishValidationErrors.processingRole)}
                    fullWidth
                    variant='outlined'
                  >
                    <InputLabel>{t(processing_activities_messages.editor.fields.processing_role)}</InputLabel>
                    <Select
                      label={t(processing_activities_messages.editor.fields.processing_role)}
                      name='processingRole'
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.processingRole}
                    >
                      <MenuItem value=''>{t(processing_activities_messages.filters.processing_role_all)}</MenuItem>
                      {PROCESSING_ROLE_OPTIONS.map(role => (
                        <MenuItem key={role} value={role}>
                          {t(processing_activities_messages.processing_role[role])}
                        </MenuItem>
                      ))}
                    </Select>
                    {publishValidationErrors.processingRole && <FormHelperText>{publishValidationErrors.processingRole}</FormHelperText>}
                  </FormControl>
                </div>
                <TextField
                  className={styles.fieldSpacing}
                  error={Boolean(publishValidationErrors.description)}
                  fullWidth
                  helperText={publishValidationErrors.description}
                  label={t(processing_activities_messages.editor.fields.description)}
                  multiline
                  name='description'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  rows={4}
                  value={formik.values.description}
                  variant='outlined'
                />
              </section>

              <section className={styles.section}>
                <Typography className={styles.sectionTitle} variant='subtitle1'>
                  {t(processing_activities_messages.editor.sections.categories)}
                </Typography>
                <ProcessingActivityCategoryField
                  error={publishValidationErrors.dataSubjectCategories}
                  fieldName='dataSubjectCategories'
                  formik={formik}
                  label={t(processing_activities_messages.editor.fields.data_subject_categories)}
                  ropaCategories={ropaCategoriesByType.data_subject}
                />
                <ProcessingActivityCategoryField
                  error={publishValidationErrors.personalDataCategories}
                  fieldName='personalDataCategories'
                  formik={formik}
                  label={t(processing_activities_messages.editor.fields.personal_data_categories)}
                  ropaCategories={ropaCategoriesByType.personal_data}
                />
                <ProcessingActivityCategoryField
                  error={publishValidationErrors.recipientCategories}
                  fieldName='recipientCategories'
                  formik={formik}
                  label={t(processing_activities_messages.editor.fields.recipient_categories)}
                  ropaCategories={ropaCategoriesByType.recipient}
                />
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHeadingRow}>
                  <div>
                    <Typography className={styles.sectionTitle} variant='subtitle1'>
                      {t(processing_activities_messages.editor.sections.purposes)}
                    </Typography>
                  </div>
                </div>
                <ProcessingActivityPurposesSection
                  formik={formik}
                  processingActivityId={processingActivityId}
                  purposesError={publishValidationErrors.purposes}
                />
              </section>

              <section className={styles.section}>
                <Typography className={styles.sectionTitle} variant='subtitle1'>
                  {t(processing_activities_messages.editor.sections.security_measures)}
                </Typography>
                <TextField
                  error={Boolean(publishValidationErrors.minimumSecurityMeasures)}
                  fullWidth
                  helperText={publishValidationErrors.minimumSecurityMeasures}
                  label={t(processing_activities_messages.editor.fields.minimum_security_measures)}
                  multiline
                  name='minimumSecurityMeasures'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  rows={4}
                  value={formik.values.minimumSecurityMeasures}
                  variant='outlined'
                />
              </section>

              <div className={styles.stickyActions}>
                <ColoredButton
                  className=''
                  customColor='grey'
                  disabled={isSaving}
                  icon={false}
                  onClick={handleNavigation}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(processing_activities_messages.editor.actions.close)}
                </ColoredButton>
                {canDiscardDraft && (
                  <ColoredButton
                    className=''
                    customColor='grey'
                    disabled={isSaving}
                    icon={false}
                    onClick={discardDraft}
                    showLoader={false}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(processing_activities_messages.editor.actions.discard_draft)}
                  </ColoredButton>
                )}
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={isSaving}
                  icon={false}
                  onClick={() => saveDraft()}
                  showLoader={isSaving}
                  textLike={false}
                  variant='outlined'
                >
                  {t(processing_activities_messages.editor.actions.save_and_continue)}
                </ColoredButton>
                <Tooltip title={!canPublish ? <span className={styles.tooltipMultiline}>{publishDisabledTooltip}</span> : ''}>
                  <Box className={styles.tooltipTrigger} component='span'>
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={isSaving || !canPublish}
                      icon={false}
                      onClick={handlePublishClick}
                      showLoader={isSaving}
                      textLike={false}
                      variant='contained'
                    >
                      {t(processing_activities_messages.editor.actions.publish_and_close)}
                    </ColoredButton>
                  </Box>
                </Tooltip>
              </div>
            </div>
          </form>
        </FormikProvider>

        <ProcessingActivityPublishDialog
          loading={isSaving}
          onClose={() => setPublishDialogOpen(false)}
          onConfirm={publishDraft}
          open={publishDialogOpen}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default ProcessingActivityEditorPage;
